"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";

import { chromeLabelLocale, chromeLocaleForPath, type ChromeLocale } from "@/lib/chrome-locale";
import type { ChromeLink, FooterChromeModel } from "@/lib/site-chrome";

/**
 * The sitewide footer's columns, profiles and legal line, in the page's
 * language. The server layout passes both language models; this picks one with
 * `usePathname()`, during SSR too, so the served HTML carries one language.
 *
 * Column labels are plain text tied to their list by `aria-labelledby`, not
 * `<h3>`s: the footer sits after the page's own headings, and seven `<h3>`s
 * with no `<h2>` above them were orphan headings in every page's outline.
 */

// inline-flex + min-h-11: a 44px tap target per link (c-a11y-12 measured them
// at 16-20px tall on every page). The list drops its own gaps to compensate.
const linkClass =
  "inline-flex min-h-11 items-center text-body-small leading-snug text-text-muted transition hover:text-text-primary hover:underline";

function FooterLink({
  link,
  pageLocale,
  placement,
}: {
  link: ChromeLink;
  pageLocale: ChromeLocale;
  placement: string;
}) {
  const labelLocale = chromeLabelLocale(link.label);
  const languageProps = {
    ...(link.lang ? { hrefLang: link.lang } : {}),
    ...(labelLocale !== pageLocale ? { lang: labelLocale } : {}),
  };
  if (link.emphasis) {
    return (
      <Link
        href={link.href}
        prefetch={false}
        data-analytics-placement={placement}
        className="inline-flex min-h-11 items-center text-body-small font-bold leading-snug text-text-primary transition hover:underline"
        {...languageProps}
      >
        {link.label}
      </Link>
    );
  }
  return (
    <Link
      href={link.href}
      prefetch={false}
      data-analytics-placement={placement}
      className={linkClass}
      {...languageProps}
    >
      {link.label}
    </Link>
  );
}

export default function SiteFooterBody({
  models,
}: {
  models: Record<ChromeLocale, FooterChromeModel>;
}) {
  const pageLocale = chromeLocaleForPath(usePathname());
  const { footerColumns, socialLabel, social, legal } = models[pageLocale];

  return (
    <div className="mx-auto max-w-6xl px-4">
      <div className="mb-10 grid grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-3 md:grid-cols-4">
        {footerColumns.map((column) => {
          const labelId = `site-footer-${column.key}`;
          return (
            <div key={column.key}>
              <p
                id={labelId}
                className="mb-3 text-eyebrow font-bold uppercase tracking-eyebrow text-text-primary"
              >
                {column.heading}
              </p>
              <ul aria-labelledby={labelId}>
                {column.links.map((link) => (
                  <li key={link.href}>
                    <FooterLink
                      link={link}
                      pageLocale={pageLocale}
                      placement={`footer_${column.key}`}
                    />
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {social.length > 0 ? (
        <nav aria-label={socialLabel} className="mb-6 flex flex-wrap items-center gap-x-5">
          {social.map((profile) => (
            <a
              key={profile.href}
              href={profile.href}
              // rel="me" is the bidirectional half of the Organization sameAs
              // claim: the graph points out, this points back.
              rel="me noopener noreferrer"
              target="_blank"
              data-analytics-placement="footer_social"
              className={linkClass}
            >
              {profile.label}
            </a>
          ))}
        </nav>
      ) : null}

      <div className="flex flex-col items-center justify-between gap-2 border-t border-border-default pt-5 sm:flex-row">
        <p className="text-caption text-text-muted">{legal.copyright}</p>
        <p className="text-caption text-text-muted">{legal.engineNote}</p>
      </div>
    </div>
  );
}
